/**
 * Hooks de consentimento dos Termos de Uso (ATR-563).
 *
 * Fonte da verdade do estado (`terms.pending`) é o `user` do auth store,
 * alimentado por `GET /auth/me`. Estes hooks só ESCREVEM — nenhum deles guarda
 * cópia local do estado de consentimento.
 */

import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { toast } from '@/hooks/use-toast';
import { getApiErrorCode } from '@/utils/api-errors';
import { useAuthStore } from '@/stores/auth.store';
import type { TermsStatus } from '@/types/auth.types';
import { termsService } from './terms.service';

function describeAcceptError(error: unknown): string {
  if (getApiErrorCode(error) === 'VALIDATION_TERMS_VERSION_MISMATCH') {
    return 'Os termos foram atualizados enquanto esta página estava aberta. Recarregue para ver a versão vigente.';
  }
  return 'Não foi possível registrar seu aceite. Tente novamente.';
}

/**
 * `POST /terms/accept`.
 *
 * No sucesso reconsulta `GET /auth/me` (`checkAuth`) em vez de escrever o
 * estado à mão: o modal desaparece porque `user.terms.pending` virou `false` na
 * fonte da verdade. Falha ⇒ toast e o modal continua aberto para nova tentativa
 * (fail-safe: sem aceite registrado, sem acesso).
 *
 * O `onError` local NÃO duplica o toast do handler global: `TOAST_LIMIT` é 1
 * (`hooks/use-toast.ts`), e o callback local roda depois do `MutationCache`, de
 * modo que é a mensagem específica daqui que fica na tela — é ela que manda o
 * usuário recarregar quando o texto mudou sob os pés dele.
 */
export function useAcceptTerms() {
  const checkAuth = useAuthStore((s) => s.checkAuth);

  return useMutation<TermsStatus, unknown, string>({
    mutationFn: (version) => termsService.accept(version),
    onSuccess: () => {
      void checkAuth();
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Não foi possível continuar',
        description: describeAcceptError(error),
      });
    },
  });
}

/**
 * `POST /terms/decline` + logout.
 *
 * O registro da recusa é BEST-EFFORT (auditoria): a falha é engolida dentro do
 * `mutationFn` para que o logout aconteça de qualquer forma — deixar o usuário
 * preso no modal porque o log de auditoria falhou seria pior do que perder a
 * linha de auditoria. O erro engolido vai para o Sentry.
 */
export function useDeclineTerms() {
  const logout = useAuthStore((s) => s.logout);

  return useMutation<void, unknown, string>({
    mutationFn: async (version) => {
      try {
        await termsService.decline(version);
      } catch (error) {
        Sentry.captureException(error, { tags: { flow: 'terms-decline' } });
      }
    },
    onSuccess: () => {
      logout();
    },
  });
}
